import styled from "@emotion/styled";
import { Button, EditModal, Input, Title } from "@src/components";
import React, { useEffect, useRef, useState } from "react";
import userNoImg from "../../assets/userNoImg.png";
import addPhotoImg from "../../assets/addPhotoImg.png";
import Cancel from "../../assets/Cancel.png";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSaveCertFileImage, useSaveProfileImage } from "@src/hooks/queries/useExpert";
import SearchAgent from "./SearchAgent";
import { Common } from "@src/styles/Common";
import {
    buildExpertUpdateCareerObject,
    ExpertProfileDetail,
    normalizeExpertProfileDetail,
    useExpertProfileDetail,
    useUpdateExpertProfile,
} from "@src/hooks/queries/useExpertDetail";

const ExpertForm = () => {
    const expertServiceList = [
        {
            value: "경매대행",
        },
        {
            value: "중개",
        },
        {
            value: "매물컨설팅",
        },
        {
            value: "임장동행",
        },
    ];

    const [isShowOfficeModal, setIsShowOfficeModal] = useState<boolean>(false);

    const profileRef = useRef<any>(null);
    const infoFileRef = useRef<any>(null);

    const { no } = useParams();
    const navigate = useNavigate();

    const { data: expertDetail, refetch: refetchExpertDatail } = useExpertProfileDetail(Number(no), "expertNo");
    const { mutate: updateExpertProfile } = useUpdateExpertProfile();

    const { mutate: saveProfileImage } = useSaveProfileImage();
    const { mutate: saveCertFileImage } = useSaveCertFileImage();
    const [tempExpertDetail, setTempExpertDetail] = useState<ExpertProfileDetail | null>(null);

    const careerFieldConfig = [
        { label: "경매대행", field: "auctionAgentCareer" as const },
        { label: "매물컨설팅", field: "consultingCareer" as const },
        { label: "임장동행", field: "companionCareer" as const },
        { label: "중개", field: "agentCareer" as const },
    ];

    const normalizePerformLabel = (value: string) => value.replace(/^#/, "").trim();

    const selectedCareerFields = careerFieldConfig.filter((career) =>
        (tempExpertDetail?.counselingCategories ?? []).some(
            (category) => normalizePerformLabel(category) === career.label,
        ),
    );

    useEffect(() => {
        setTempExpertDetail(null);
    }, [no]);

    useEffect(() => {
        if (expertDetail && tempExpertDetail === null) {
            setTempExpertDetail(normalizeExpertProfileDetail(expertDetail));
        }
    }, [expertDetail, tempExpertDetail]);

    const phoneNumRex = /^(010|011|016|018|019)/;

    const handleSubmit = () => {
        if (!tempExpertDetail) {
            return;
        }

        updateExpertProfile(
            {
                expertNo: tempExpertDetail?.expertNo,
                phone: tempExpertDetail?.phone,
                profile: tempExpertDetail?.profileImageUrl,
                bankName: tempExpertDetail?.bankName,
                bankAccount: tempExpertDetail?.bankAccount,
                career: buildExpertUpdateCareerObject(tempExpertDetail, tempExpertDetail?.counselingCategories ?? []),
                qualification: tempExpertDetail?.qualification ?? "",
                introduction: tempExpertDetail?.intro ?? "",
                counselingCategories: tempExpertDetail?.counselingCategories ?? [],
                auctionAgentCertFileUrl: tempExpertDetail?.auctionAgentRegistrationImage ?? "",
                officeInfo: {
                    name: tempExpertDetail?.office?.name ?? "",
                    registrationNumber: tempExpertDetail?.office?.registrationNumber ?? "",
                    ceo: tempExpertDetail?.office?.ceo ?? "",
                    phone: tempExpertDetail?.office?.phone ?? "",
                    address: tempExpertDetail?.office?.address ?? "",
                },
                isAuctionAgent: !!tempExpertDetail?.auctionAgentRegistrationImage,
            },
            {
                onSuccess: () => {
                    alert("수정되었습니다.");
                },
            },
        );
    };

    return (
        <>
            <StyledExpertFormWrap>
                <Title title={no ? "전문가 회원 수정" : "전문가 회원 등록"} />
                <div className="contents">
                    <div className="userNumContents">
                        {no && (
                            <div className="changeHistory">
                                <Button
                                    color="purple"
                                    size="medium"
                                    label={no ? "수정하기" : "등록하기"}
                                    isDisabled={false}
                                    handleClick={() => {
                                        handleSubmit();
                                    }}
                                />
                            </div>
                        )}
                    </div>
                    <>
                        <div className="userInfo">
                            <div className="userImgWrap">
                                <img
                                    src={tempExpertDetail?.profileImageUrl ?? userNoImg}
                                    alt=""
                                    className="userImg"
                                    onClick={() => {
                                        profileRef.current.click();
                                    }}
                                />
                                <input
                                    type="file"
                                    id="fileUpload"
                                    ref={profileRef}
                                    onChange={(e) => {
                                        if (e.target.files && expertDetail?.expertNo) {
                                            saveProfileImage(
                                                {
                                                    file: e.target.files[0],
                                                    expertNo: expertDetail?.expertNo,
                                                },
                                                {
                                                    onSuccess: (data) => {
                                                        if (tempExpertDetail) {
                                                            setTempExpertDetail({
                                                                ...tempExpertDetail,
                                                                profileImageUrl: data,
                                                            });
                                                        }
                                                    },
                                                },
                                            );
                                        }
                                    }}
                                />
                            </div>
                            <div className="userTextDiv">
                                <Input
                                    handleChange={(evt) => {}}
                                    label="이름"
                                    placeholder="미스고"
                                    size="dynamic"
                                    type="text"
                                    value={tempExpertDetail?.name ?? ""}
                                    border={true}
                                    isDisabled={true}
                                />
                                <div className="requiredInputDiv">
                                    <div className="requiredInputTitle">
                                        연락처
                                        <span className="required">*</span>
                                        <span className="addMessage">
                                            고객과 소통하는 번호로 입력 (문자/전화 문의용)
                                        </span>
                                    </div>
                                    <Input
                                        handleChange={(evt) => {
                                            if (tempExpertDetail) {
                                                setTempExpertDetail({
                                                    ...tempExpertDetail,
                                                    phone: evt.target.value,
                                                });
                                            }
                                        }}
                                        label=""
                                        placeholder="01000000000( - 없이 입력해주세요)"
                                        size="dynamic"
                                        type="text"
                                        value={tempExpertDetail?.phone ?? ""}
                                        border={true}
                                    />
                                    {tempExpertDetail?.phone &&
                                        !phoneNumRex.test(tempExpertDetail?.phone.slice(0, 3)) && (
                                            <span className="errorMessage">010으로 시작되는 번호로 입력해 주세요</span>
                                        )}
                                </div>

                                <Input
                                    handleChange={(evt) => {
                                        if (tempExpertDetail) {
                                            setTempExpertDetail({
                                                ...tempExpertDetail,
                                                bankName: evt.target.value,
                                            });
                                        }
                                    }}
                                    label="은행"
                                    placeholder="미스고"
                                    size="small"
                                    type="text"
                                    value={tempExpertDetail?.bankName ?? "-"}
                                    border={true}
                                    isDisabled={false}
                                />
                                <Input
                                    handleChange={(evt) => {
                                        if (tempExpertDetail) {
                                            setTempExpertDetail({
                                                ...tempExpertDetail,
                                                bankAccount: evt.target.value,
                                            });
                                        }
                                    }}
                                    label="계좌번호"
                                    placeholder="미스고"
                                    size="dynamic"
                                    type="text"
                                    value={tempExpertDetail?.bankAccount ?? "-"}
                                    border={true}
                                    isDisabled={false}
                                />
                            </div>
                        </div>
                        <div className="officeInfo">
                            <div className="searchInfoWrap">
                                <div style={{ display: "flex" }}>
                                    <Title title="중개사무소(중개법인) 정보" />
                                    <span className="required">*</span>
                                    <Button
                                        color="white"
                                        size="small"
                                        label="조회하기 "
                                        isDisabled={false}
                                        styles={{ marginLeft: 10 }}
                                        handleClick={() => {
                                            setIsShowOfficeModal(true);
                                        }}
                                    />
                                </div>

                                {tempExpertDetail?.office?.name && (
                                    <div className="officeBox">
                                        <div className="officeRow">
                                            <span className="name">사무소명</span>
                                            <span className="text">{tempExpertDetail?.office?.name}</span>
                                        </div>

                                        <div className="officeRow">
                                            <span className="name">중개등록번호</span>
                                            <span className="text">{tempExpertDetail?.office?.registrationNumber}</span>
                                        </div>

                                        <div className="officeRow">
                                            <span className="name">대표</span>
                                            <span className="text">{tempExpertDetail?.office?.ceo}</span>
                                        </div>
                                        <div className="officeRow">
                                            <span className="name">전화번호</span>
                                            <span className="text">{tempExpertDetail?.office?.phone}</span>
                                        </div>
                                        <div className="officeRow">
                                            <span className="name">소재지</span>
                                            <span className="text">{tempExpertDetail?.office?.address}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="counselingField">
                                <div className="titleWrap">
                                    <Title title="수행기능 (중복 선택 가능)" />
                                </div>
                                <div className="buttonDiv">
                                    {expertServiceList.map((item) => {
                                        return (
                                            <Button
                                                key={item.value}
                                                color={
                                                    tempExpertDetail?.counselingCategories?.some((category) =>
                                                        category.includes(item.value),
                                                    )
                                                        ? "purple"
                                                        : "gray"
                                                }
                                                size="small"
                                                label={item.value}
                                                isDisabled={false}
                                                handleClick={() => {
                                                    if (tempExpertDetail) {
                                                        const tags = tempExpertDetail.counselingCategories ?? [];
                                                        setTempExpertDetail({
                                                            ...tempExpertDetail,
                                                            counselingCategories: tags.some((category) =>
                                                                category.includes(item.value),
                                                            )
                                                                ? tags.filter((text) => !text.includes(item.value))
                                                                : [...tags, `${item.value}`],
                                                        });
                                                    }
                                                }}
                                            />
                                        );
                                    })}
                                </div>

                                <div className="officePhotoWrap">
                                    <Title title="매수신청대리인 등록증" />
                                    <input
                                        type="file"
                                        id="infoFileUpload"
                                        style={{ display: "none" }}
                                        ref={infoFileRef}
                                        onChange={(e) => {
                                            if (e.target.files && expertDetail?.expertNo) {
                                                saveCertFileImage(
                                                    {
                                                        file: e.target.files[0],
                                                        expertNo: expertDetail?.expertNo,
                                                    },
                                                    {
                                                        onSuccess: (data) => {
                                                            if (tempExpertDetail) {
                                                                setTempExpertDetail({
                                                                    ...tempExpertDetail,
                                                                    auctionAgentRegistrationImage: data,
                                                                });
                                                            }
                                                        },
                                                    },
                                                );
                                            }
                                        }}
                                    />
                                    <div className="imgList">
                                        <img
                                            src={addPhotoImg}
                                            alt=""
                                            onClick={() => {
                                                infoFileRef.current.click();
                                            }}
                                        />
                                        <div className="fileCount">
                                            {tempExpertDetail?.auctionAgentRegistrationImage ? "(1/1)" : "(0/1)"}
                                        </div>
                                        {tempExpertDetail?.auctionAgentRegistrationImage && (
                                            <img src={tempExpertDetail?.auctionAgentRegistrationImage} alt="" />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="textBox">
                            <div className="textItem">
                                <Title title="경력" />
                                <div className="careerList">
                                    {selectedCareerFields.map((career) => (
                                        <div className="careerRow" key={career.field}>
                                            <span className="careerLabel">{career.label}</span>
                                            <div className="careerInputWrap">
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    value={
                                                        tempExpertDetail?.[career.field] === 0
                                                            ? ""
                                                            : tempExpertDetail?.[career.field] ?? ""
                                                    }
                                                    onChange={(e) => {
                                                        if (!tempExpertDetail) {
                                                            return;
                                                        }

                                                        const numericValue = e.target.value.replace(/[^0-9]/g, "");
                                                        setTempExpertDetail({
                                                            ...tempExpertDetail,
                                                            [career.field]:
                                                                numericValue === "" ? 0 : Number(numericValue),
                                                        });
                                                    }}
                                                />
                                                <span className="yearText">년</span>
                                            </div>
                                        </div>
                                    ))}
                                    {selectedCareerFields.length === 0 && (
                                        <p className="emptyCareer">수행기능에서 선택된 항목이 없습니다.</p>
                                    )}
                                </div>
                            </div>
                            <div className="textItem">
                                <Title title="자격" />
                                <textarea
                                    placeholder="공인중개사 외의 다른 자격 사항이 있으시면 기재해 주세요(최대200자)"
                                    onChange={(e) => {
                                        if (tempExpertDetail) {
                                            setTempExpertDetail({
                                                ...tempExpertDetail,
                                                qualification: e.target.value,
                                            });
                                        }
                                    }}
                                    value={tempExpertDetail?.qualification ?? ""}
                                />
                            </div>
                            <div className="textItem">
                                <Title title="소개" />
                                <textarea
                                    className="introduce"
                                    placeholder="전문가님을 기다리는 회원분들께 소개의 글을 작성해 주세요(최대500자)"
                                    onChange={(e) => {
                                        if (tempExpertDetail) {
                                            setTempExpertDetail({
                                                ...tempExpertDetail,
                                                intro: e.target.value,
                                            });
                                        }
                                    }}
                                    value={tempExpertDetail?.intro ?? ""}
                                />
                            </div>
                        </div>
                    </>
                </div>
            </StyledExpertFormWrap>

            <EditModal
                buttonText=""
                handleBtnClick={() => {}}
                handleOpenModal={() => setIsShowOfficeModal(false)}
                size="big"
                title="중개사무소 조회"
                isModalShow={isShowOfficeModal}
                dimClose={true}
                checkCloseModal={true}
                deleteButton={true}
            >
                <>
                    <SearchAgent setExpertData={setTempExpertDetail} setIsShowOfficeModal={setIsShowOfficeModal} />
                </>
            </EditModal>
        </>
    );
};

export default ExpertForm;

const StyledExpertFormWrap = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 40px;
    gap: 20px;
    box-sizing: border-box;
    .contents {
        display: flex;
        flex-direction: column;
        .userNumContents {
            display: flex;
            gap: 20px;
            .userNo {
                display: flex;
                align-items: center;
            }
            .changeHistory {
                display: flex;
                gap: 10px;
                align-items: center;
                flex-direction: column;
                position: absolute;
                top: 80px;
                right: 40px;
            }
        }
        .userInfo {
            display: flex;
            gap: 40px;
            margin-top: 57px;
            .userImgWrap {
                display: flex;
                flex-direction: column;
                gap: 32px;
                align-items: center;
                position: relative;
                .userImg {
                    width: 110px;
                    height: 110px;
                    border-radius: 55px;
                }
                .photo {
                    width: 40px;
                    height: 40px;
                    position: absolute;
                    left: 181px;
                    bottom: 74px;
                }
                .buttonDiv {
                    display: flex;
                    gap: 10px;
                    flex-wrap: nowrap;
                }
            }
            .userTextDiv {
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                gap: 8px;
                & > input {
                    width: 360px;
                    height: 42px;
                    background-color: #fff;
                }
                .requiredInputDiv {
                    display: flex;
                    flex-direction: column;
                    gap: 9px;
                    & > input {
                        width: 360px;
                        height: 42px;
                    }
                    .requiredInputTitle {
                        display: flex;
                        font-size: 14px;
                        font-weight: 500;
                        line-height: 140%;
                        .required {
                            line-height: 22px;
                            font-size: 30px;
                            font-weight: 700;
                            display: flex;
                            align-items: flex-start;
                            color: #ff0000;
                        }
                        .addMessage {
                            font-size: 10px;
                            font-weight: 400;
                            line-height: 12px;
                            color: #ff0000;
                            padding-left: 13px;
                            display: flex;
                            align-items: center;
                        }
                    }
                    .errorMessage {
                        font-size: 12px;
                        font-weight: 400;
                        line-height: 12px;
                        color: #ff0000;
                    }
                }
            }
        }
        .officeInfo {
            display: flex;
            gap: 108px;
            margin-top: 70px;
            .searchInfoWrap {
                display: flex;
                flex-direction: column;
                gap: 16px;
                .required {
                    line-height: 22px;
                    font-size: 30px;
                    font-weight: 700;
                    display: flex;
                    align-items: flex-start;
                    color: #ff0000;
                    padding-left: 5px;
                }
                .officeBox {
                    width: 400px;
                    padding: 20px 0;
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    justify-content: center;
                    flex-direction: column;

                    .officeRow {
                        display: flex;
                        width: 100%;
                        gap: 30px;
                        padding-bottom: 10px;
                        border-bottom: 1px solid ${Common.colors.gray200};
                        line-height: 1.2;
                        .name {
                            color: ${Common.colors.gray500};
                            width: 100px;
                            min-width: 100px;
                            font-size: 14px;
                        }
                    }
                }
            }
            .officePhotoWrap {
                display: flex;
                flex-direction: column;
                gap: 10px;
                margin-top: 40px;
                & > p {
                    font-size: 10px;
                    font-weight: 400;
                    line-height: 12px;
                }
                .imgList {
                    display: flex;
                    gap: 10px;
                    position: relative;
                    .fileCount {
                        position: absolute;
                        bottom: 10px;
                        left: 24px;
                    }
                    & > img {
                        width: 85px;
                        height: 85px;

                        object-fit: cover;
                    }
                }
            }
        }
        .counselingField {
            display: flex;
            flex-direction: column;
            gap: 20px;

            .titleWrap {
                display: flex;
                flex-direction: column;
                gap: 6px;
                & > p {
                    font-size: 10px;
                    font-weight: 400;
                    line-height: 12px;
                }
            }
            .buttonDiv {
                display: flex;

                gap: 14px;
                .buttonRow {
                    display: flex;
                    gap: 14px;
                    align-items: center;
                }
            }
        }
        .consultationFee {
            display: flex;
            flex-direction: column;
            gap: 24px;
            margin-top: 80px;
            .required {
                line-height: 22px;
                font-size: 30px;
                font-weight: 700;
                display: flex;
                align-items: flex-start;
                color: #ff0000;
                padding-left: 5px;
            }
            .contentsWrap {
                display: flex;
                gap: 20px;
                .inputWrap {
                    display: flex;
                    flex-direction: column;
                    width: fit-content;
                    position: relative;
                    gap: 8px;
                    .inputRow {
                        display: flex;
                        gap: 18px;
                        .name {
                            font-size: 18px;
                            font-weight: 400;
                            line-height: 22px;
                            width: 80px;
                            color: #8d8d8d;
                            :first-of-type {
                                padding-top: 19px;
                            }
                        }
                        .inputContents {
                            display: flex;
                            flex-direction: column;
                            gap: 8px;
                        }
                        & > .inputContents {
                            & > div {
                                & > label {
                                    & > select {
                                        color: #222222;
                                        font-weight: 400;
                                        font-size: 14px;
                                        border: 1px solid #b4b4b9;
                                        border-radius: 10px;
                                        width: 328px;
                                        height: 56px;
                                    }
                                }
                            }
                        }
                        .textInput {
                            width: 328px;
                            height: 56px;
                            background-color: #f0f0f0;
                            border-radius: 10px;
                            border: none;
                        }
                    }
                }
                .tableArea {
                    & > table {
                        width: 500px;
                        border: 2px solid #f0f0f0;
                        border-collapse: collapse;
                        & > thead {
                            width: 500px;
                            height: 60px;
                            & > tr {
                                width: 100%;
                                height: 100%;
                                background-color: #d9d9d9;
                                & > th {
                                    text-align: center;
                                    vertical-align: middle;
                                    :nth-of-type(1) {
                                        width: 200px !important;
                                    }
                                    :nth-of-type(2) {
                                        width: 250px !important;
                                    }
                                }
                            }
                        }
                        & > tbody {
                            height: fit-content;
                            & > tr {
                                width: 500px;
                                height: 60px;
                                & > td {
                                    text-align: center;
                                    vertical-align: middle;
                                    padding: 14px;
                                    & > img {
                                        width: 26px;
                                        height: 26px;
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        .textBox {
            display: flex;
            flex-direction: column;
            gap: 40px;
            margin-top: 100px;
            .textItem {
                display: flex;
                flex-direction: column;
                .careerList {
                    width: 908px;
                    margin-top: 20px;
                    background-color: transparent;
                    padding: 0;
                    box-sizing: border-box;
                    .careerRow {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        border-bottom: 1px solid ${Common.colors.gray200};
                        padding: 20px 0;
                        &:first-of-type {
                            padding-top: 0;
                        }
                        &:last-of-type {
                            border-bottom: none;
                            padding-bottom: 0;
                        }
                        .careerLabel {
                            font-size: 18px;
                            font-weight: 700;
                            line-height: 1.2;
                        }
                        .careerInputWrap {
                            display: flex;
                            align-items: center;
                            gap: 8px;
                            & > input {
                                width: 80px;
                                border: 1px solid ${Common.colors.gray300};
                                border-radius: 6px;
                                background-color: transparent;
                                font-size: 18px;
                                font-weight: 400;
                                line-height: 1.2;
                                text-align: right;
                                padding: 6px 8px;
                                box-sizing: border-box;
                                outline: none;
                            }
                            .yearText {
                                font-size: 18px;
                                font-weight: 400;
                                line-height: 1.2;
                                color: ${Common.colors.gray600};
                            }
                        }
                    }
                    .emptyCareer {
                        margin: 0;
                        color: ${Common.colors.gray500};
                        font-size: 14px;
                    }
                }
                & > textarea {
                    width: 908px;
                    height: 116px;
                    background-color: #f0f0f0;
                    border-radius: 10px;
                    border: none;
                    padding: 14px 18px;
                    resize: none;
                }
                .introduce {
                    height: 245px;
                }
            }
        }
    }
`;
